<script setup lang="ts">
  import { ref as vueRef, defineEmits } from 'vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref, watch } from 'vue'
  import {
    AiAugmentedByMapperKeysEnum as DatamapperKey,
    type CustomerCase as Case
  } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import Button from '@/components/ui/button/Button.vue'
  import { Loader2 } from 'lucide-vue-next'
  import { useToast } from '@/components/ui/toast'
  import { storeToRefs } from 'pinia'

  const emit = defineEmits(['close'])

  // Translations
  const { t } = useTranslation({
    en: {
      replacementCost: 'Replacement Cost',
      expertCosts: 'Expert Costs',
      lumpSum: 'Lump Sum',
      totalDamage: 'Residual damage (Total damage)',
      result: 'Result',
      save: 'Save',
      updatedFields: 'Updated Fields'
    },
    de: {
      replacementCost: 'Wiederbeschaffungsaufwand',
      expertCosts: 'Sachverständigenkosten',
      lumpSum: 'Kostenpauschale',
      totalDamage: 'Restschaden (Totalschaden)',
      result: 'Ergebnis',
      save: 'Speichern',
      updatedFields: 'Felder aktualisiert'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const isLoading = computed(() => !caseRecord.value?.data)
  const { isSavingCase } = storeToRefs(caseStore)

  // Input fields with store bindings
  const replacementCost = ref<number>(0)
  const expertCost = ref<number>(0)
  const lumpSum = ref<number>(0)

  // Load initial data
  const loadInitialData = async () => {
    const replacementCostValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_COST
    )
    replacementCost.value = replacementCostValue ? parseFloat(replacementCostValue) : 0

    const expertCostsValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_EXPERT_COST
    )
    expertCost.value = expertCostsValue ? parseFloat(expertCostsValue) : 0

    const lumpSumValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_COST_PER_SCALE
    )
    lumpSum.value = lumpSumValue ? parseFloat(lumpSumValue) : 25
  }

  loadInitialData()

  const formatToTwoDecimals = (value: number): number => {
    return Number((value || 0).toFixed(2))
  }

  const { toast } = useToast()

  const handleSaveAll = async () => {
    await caseStore.bulkUpdateDataWithMapperKeys({
      [DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_COST]: replacementCost.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_EXPERT_COST]: expertCost.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_COST_PER_SCALE]: lumpSum.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_LOSS_AMOUNT_TOTAL_DAMAGE_CASE]: totalDamage.value
    })
    toast({
      title: t('updatedFields'),
      description: 'Total damage fields updated',
      variant: 'default'
    })
    emit('close')
  }

  const totalDamage = computed(() => {
    const sum = Number(replacementCost.value) + Number(expertCost.value) + Number(lumpSum.value)
    return formatToTwoDecimals(sum)
  })
</script>

<template>
  <div class="mt-2 flex h-full flex-col overflow-hidden">
    <div class="max-h-[67vh] flex-1 space-y-4 overflow-y-auto">
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <InputField
            v-model="replacementCost"
            :label="t('replacementCost')"
            name="replacement-cost"
            type="number"
            suffix="€"
            asterisk
          />
          <InputField
            v-model="expertCost"
            :label="t('expertCosts')"
            name="expert-costs"
            type="number"
            suffix="€"
            asterisk
          />
          <InputField
            v-model="lumpSum"
            :label="t('lumpSum')"
            name="lump-sum"
            type="number"
            suffix="€"
            asterisk
          />
        </div>
      </div>
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md p-3">
          <div class="col-span-2 flex h-full items-end gap-2">
            <div class="flex-grow">
              <InputField
                v-model="totalDamage"
                :label="t('totalDamage')"
                name="total-damage"
                :disabled="true"
                class="w-full font-medium text-green-600"
                suffix="€"
              />
            </div>

            <Button
              :class="[
                'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white',
                'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
                isSavingCase
                  ? 'cursor-not-allowed bg-purple-400'
                  : 'bg-purple-600 hover:bg-purple-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              ]"
              :disabled="isSavingCase"
              @click="handleSaveAll"
            >
              <span v-if="isSavingCase">
                <Loader2 class="mr-1 inline h-4 w-4 animate-spin align-text-bottom" />
                {{ t('save') }}
              </span>
              <span v-else>
                {{ t('save') }}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
